import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import regionConfig from 'src/config';
import { useCall } from '../../customer-call-sdk';
import { RootState } from '../../store';
import { showNumberSelectionDialog } from '../../store/customer/actions';
import { CustomerState } from '../../store/customer/types';
import { setSidebarScreen } from '../../store/sidebar/actions';
import ActiveCustomerNumber from '../ActiveCustomerNumber';
import Button from '../common/Button';
import CloseIcon from '../common/Icons/Close';
import PhoneNumberInput from '../common/PhoneNumberInput';

const StyledDialpad = styled.div`
    width: 100%;
    height: 100%;
    padding: 1.5rem 2rem;
    overflow-y: auto;

    .dialpad-header {
        position: relative;

        display: flex;
        flex-direction: column;
        margin-bottom: 1.25rem;
    }

    .close-icon {
        width: 32px;
        height: 32px;
        cursor: pointer;
        position: absolute;
        right: 0px;
        top: 0px;
    }
    .enter-number-label {
        color: var(--text-primary);
        font-size: 2.125rem;
        font-weight: bold;
        line-height: 2.5rem;
    }
    .credit-per-minute-label {
        color: rgba(17, 17, 17, 0.5);
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.75rem;
    }
    .input-container {
        margin-top: 6rem;
        margin-bottom: 7rem;
    }
    .phonenumber-input {
        width: 100%;
        color: var(--text-primary);
        font-size: 2.8rem;
        font-weight: 700;
        text-align: center;
        outline: none;
        border-radius: 0.5rem;
        border: 2px solid transparent;
    }
    .phonenumber-input:placeholder-shown {
        color: rgba(17, 17, 17, 0.5);
    }
    .phonenumber-input:hover {
        border: 2px solid #1884eb;
    }
    .phonenumber-input:focus {
        border: 2px solid #ccc;
    }

    .active-number-container {
        width: 100%;
        margin: auto;
        cursor: pointer;
    }

    @media screen and (max-height: 700px) {
        .input-container {
            margin-top: 3rem;
            margin-bottom: 3.5rem;
        }
    }
`;

const Dialpad: React.FC = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { activeCustomerNumber, customerNumbers } = useSelector<RootState, CustomerState>(
        (state) => state.customerDetail,
    );
    const call = useCall();

    const [phoneNumber, setPhoneNumber] = useState('');

    const showSidebarMenu = () => {
        dispatch(setSidebarScreen('menu'));
    };

    const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = event;
        const { rawValue } = (target as unknown) as { value: string; rawValue: string };
        setPhoneNumber(rawValue);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text') || '';
        e.preventDefault();
        const s = text.replace(/[^\d]/g, '').slice(0, 14);
        setPhoneNumber(s);
    };

    const handleCallClick = async () => {
        if (activeCustomerNumber) {
            setIsLoading(true);
            await call.makeCall({ toPhoneNumber: phoneNumber });
            showSidebarMenu();
            setIsLoading(false);
        }
    };

    const disableCallButton = phoneNumber.length < regionConfig.minPhoneLength;

    return (
        <StyledDialpad>
            <div className="dialpad-header" aria-label="Close">
                <div className="enter-number-label">Enter a number</div>
                <div className="credit-per-minute-label">1 credit/min</div>
                <CloseIcon onClick={showSidebarMenu} className="close-icon" />
            </div>

            <div className="input-container" aria-required="true">
                <PhoneNumberInput
                    className="phonenumber-input"
                    value={phoneNumber}
                    onChange={handlePhoneInputChange}
                    onPaste={handlePaste}
                />
            </div>
            <div className="active-number-wrapper" aria-label="Back to Account Picker">
                {activeCustomerNumber && (
                    <ActiveCustomerNumber
                        activeNumber={activeCustomerNumber}
                        totalOwnedNumber={customerNumbers.length}
                        className="active-number-container"
                        onClick={() => dispatch(showNumberSelectionDialog())}
                    />
                )}
            </div>
            <Button
                size="large"
                fullWidth
                disabled={isLoading || disableCallButton}
                onClick={handleCallClick}
                aria-label="Call"
            >
                Call
            </Button>
        </StyledDialpad>
    );
};

export default Dialpad;
